// KeyboardShortcutsProvider.tsx - Provider global para atalhos
// v2.2 - Janeiro 2026 - ⌘+⇧+Backspace para Esvaziar Lixeira
import React, { useState, useEffect, ReactNode } from 'react';
import { useLocation } from 'wouter';
import { useKeyboardShortcuts } from '../hooks/useKeyboardShortcuts';
import { useDeviceClass } from '../hooks/useDeviceClass';
import { KeyboardShortcutsModal } from './KeyboardShortcutsModal';
import { useListNavigation } from './ListNavigationContext';
import { useQuickFindStore } from '../stores/quickFindStore';
import { useBigwaSearchStore } from '../stores/bigwaSearchStore';

interface KeyboardShortcutsProviderProps {
  children: ReactNode;
}

// Contador de instâncias montadas — consultado pelo fallback de ⌘K/⌘⇧K do App.tsx
// (superfícies sem shell, onde este Provider não monta). Objeto mutável module-scoped:
// quem importa lê sempre o valor corrente.
export const dashboardShortcutsMounted = { count: 0 };

export function KeyboardShortcutsProvider({ children }: KeyboardShortcutsProviderProps) {
  const [showHelpModal, setShowHelpModal] = useState(false);
  const listNav = useListNavigation();
  const [location] = useLocation();
  const { isTouch, isTabletSize } = useDeviceClass();
  const isIPadMode = isTouch && isTabletSize;

  useEffect(() => {
    dashboardShortcutsMounted.count += 1;
    return () => {
      dashboardShortcutsMounted.count -= 1;
    };
  }, []);

  const { waitingForNavKey, navKeys } = useKeyboardShortcuts({
    onShowHelp: () => setShowHelpModal(true),
    onGlobalSearch: () => {
      useQuickFindStore.getState().open();
    },
    // ⌘K — Bigwa Search em Leituras (desktop); QuickFind nas demais páginas/iPad
    onPrimarySearch: () => {
      if (location === '/leituras' && !isIPadMode) {
        useBigwaSearchStore.getState().toggle();
      } else {
        useQuickFindStore.getState().open();
      }
    },
    onNewTask: () => {
      const btn = document.querySelector('[data-new-task]') as HTMLButtonElement;
      btn?.click();
    },
    onShowFavoritos: () => {
      const btn = document.querySelector('[data-show-favoritos]') as HTMLButtonElement;
      btn?.click();
    },
    onNewReference: () => {
      const btn = document.querySelector('[data-add-reference]') as HTMLButtonElement;
      btn?.click();
    },
    onSync: () => {
      const btn = document.querySelector('[data-sync-button]') as HTMLButtonElement;
      btn?.click();
    },
    onToggleGlobalInspector: () => {
      window.dispatchEvent(new CustomEvent('bigwa:toggle-inspector'));
    },
    // ⌘\ — recolhe/expande a barra lateral via evento global
    onToggleSidebar: () => {
      window.dispatchEvent(new CustomEvent('bigwa:toggle-sidebar'));
    },
    // NOVO: Cmd+, abre Preferências via evento
    onOpenPreferences: () => {
      window.dispatchEvent(new CustomEvent('bigwa:open-preferences'));
    },
    // ⌘+? (⌘+Shift+/) abre Bússola via evento global
    onOpenBussola: () => {
      window.dispatchEvent(new CustomEvent('bigwa:open-bussola'));
    },
    // NOVO: ⌘+⇧+Backspace esvazia lixeira via evento global
    onEmptyTrash: () => {
      window.dispatchEvent(new CustomEvent('bigwa:empty-trash'));
    },
    listActions: {
      hasList: listNav.state.items.length > 0,
      focusNext: listNav.focusNext,
      focusPrev: listNav.focusPrev,
      openFocused: listNav.openFocused,
      deleteFocused: listNav.deleteFocused,
      toggleInspector: listNav.toggleInspector,
      setInspectorTab: listNav.setInspectorTab,
    },
  });

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && showHelpModal) {
        setShowHelpModal(false);
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [showHelpModal]);

  return (
    <>
      {children}
      <KeyboardShortcutsModal isOpen={showHelpModal} onClose={() => setShowHelpModal(false)} />
      {waitingForNavKey && (
        <div className="nav-key-indicator">
          <span className="nav-key-indicator-label">Ir para:</span>
          <div className="nav-key-indicator-keys">
            {navKeys.map((k) => <kbd key={k}>{k}</kbd>)}
          </div>
        </div>
      )}
    </>
  );
}
